import { Img } from "materiajs/elements";

/**
 * Class representing a responsive image.
 */
export class ResponsiveImg extends Img {
  /**
   * Creates an instance of ResponsiveImg.
   *
   * @param {Object} params - The parameters for the responsive image.
   */
  constructor(params) {
    super(params);

    this.loading = params.loading || "lazy";

    const keys = ["src_xs", "src_sm", "src_md", "src_lg", "src_xl"];
    const srcset = [];

    const widthMap = {
      src_xs: "100w",
      src_md: "768w",
    };

    const sizesMap = {
      src_xs: "(max-width: 768px) 100w",
      src_md: "768w",
    };

    const encodeSpaces = (url) => url?.replace(/ /g, "%20");

    let fullSizeSet = false;

    // Store all src values as data-src attributes
    keys.forEach((key) => {
      const setValue = (dataKey, key) => {
        this[`data-${dataKey.replace("_", "")}`] = encodeSpaces(params[key]);
      };

      if (params[key]) {
        setValue(key, key);
      } else if (!fullSizeSet) {
        setValue(key, "src");
        fullSizeSet = true;
      }
    });

    // Add xs and md to srcset
    if (params.src_xs) {
      srcset.push(`${encodeSpaces(params.src_xs)} ${widthMap.src_xs}`);
    }
    if (params.src_md) {
      srcset.push(`${encodeSpaces(params.src_md)} ${widthMap.src_md}`);
    }

    // Set the srcset attribute if multiple sources are provided
    if (srcset.length > 0) {
      this.srcset = srcset.join(", ");
      this.sizes = sizesMap.src_xs + ", " + sizesMap.src_md;
    }

    this.load = (element) => {
      const width = element.offsetWidth;

      // Set the src attribute based on the width of the img element
      const widths = [0, 100, 400, 768, 1024, 1440],
        keys = ["xs", "sm", "md", "lg", "xl"];

      // Get all data-src* attributes from the element
      const availableSizes = keys.filter((key) => element.dataset[`src${key}`]);

      // Set the src attribute based on the width of the img element
      for (let i = widths.length - 1; i >= 0; i--) {
        if (width > widths[i] && availableSizes.includes(keys[i])) {
          element.src = element.dataset[`src${keys[i]}`];
          break;
        }
      }

      // and remove the srcset attribute
      element.removeAttribute("srcset");
    };
  }
}

/**
 * Creates a lazy responsive image component.
 *
 * @param {Object} options - The options for the lazy responsive image.
 * @param {string} options.src - The source URL of the image.
 * @param {string} [options.alt=""] - The alt text for the image.
 * @param {string} [options.className=""] - Additional class names for the image.
 * @param {Object} [options.breakpoints] - The breakpoints for the responsive image.
 * @returns {LazyResponsiveImg} The lazy responsive image component.
 */
export class LazyResponsiveImg extends ResponsiveImg {
  constructor(params) {
    super(params);

    this.loading = "lazy";
  }
}
